import {
  BulkProjectMappingRequest,
  BulkProjectMappingResponse,
  BulkResetMappingsRequest,
  EffectiveMappingsResponse,
  EffectiveSpeciesListResponse,
  EnabledModelItem,
  EnabledModelsResponse,
  OrganizationUserDto,
  PaginatedPredictionRunResponse,
  ProjectCreateDto,
  ProjectMappingCreateRequest,
  ProjectMappingResponse,
  ProjectMappingUpdateRequest,
  ProjectResponseDto,
  SpeciesListItemCreateRequest,
  SpeciesListItemResponse,
} from "../components/common/ObjectTypes";
import {
  ProjectSearchQueryType,
  ProjectUserSearchQueryType,
} from "../components/common/QueryTypes";
import axios from "./axios";

export async function getProjects(
  query: ProjectSearchQueryType,
): Promise<{ projects: ProjectResponseDto[]; count: number }> {
  try {
    const resp = await axios.get("/projects", {
      params: query,
    });
    return resp.data;
  } catch (error) {
    console.error("Error fetching projects:", error);
    throw error;
  }
}

export async function createProject(
  data: ProjectCreateDto,
): Promise<ProjectResponseDto> {
  try {
    const resp = await axios.post("/projects", data);
    return resp.data;
  } catch (err) {
    console.error(err);
    throw err;
  }
}

/**
 * The IANA timezone identifiers the API will accept for a project's timezone.
 *
 * Fetched from the API rather than derived in the browser (e.g. from
 * `Intl.supportedValuesOf("timeZone")`) on purpose: the server validates against its own
 * tzdata, and a browser whose tz database differs would let the user pick a zone that is
 * then rejected on save. Asking the API guarantees the picker and the validator agree.
 *
 * Language-independent, unlike `getCountries` — these are technical identifiers that must
 * be sent back verbatim, not localized display names, so there is nothing to refetch when
 * the user changes language.
 * @returns the sorted list of valid identifiers
 */
export async function getProjectTimezones(): Promise<string[]> {
  try {
    const resp = await axios.get("/projects/timezones");
    return resp.data.timezones;
  } catch (err) {
    console.error(err);
    throw err;
  }
}

export async function getProject(
  projId: number,
): Promise<ProjectResponseDto | undefined> {
  try {
    const resp = await axios.get(`/projects/${projId}`);
    return resp.data;
  } catch (err) {
    console.error(err);
  }
}

/**
 * Read one project by its guid.
 *
 * Rethrows the interceptor's rejection rather than swallowing it (AKL-939): the API answers
 * 403 for a user who may not read the project, and the details page tells that apart from
 * "not found" to show the no-access state. Callers that relied on `undefined` for any failure
 * (the AI Processing and Media Management deep links) catch the rejection themselves and keep
 * their "Project not found." path; every other caller already sat in a try/catch.
 * @param projGuid the project's guid
 * @returns the project; `undefined` only if the API answered 2xx with no body
 */
export async function getProjectByGuid(
  projGuid: string,
): Promise<ProjectResponseDto | undefined> {
  try {
    const resp = await axios.get(`/projects/guid/${projGuid}`);
    return resp.data;
  } catch (err) {
    console.error(err);
    throw err;
  }
}

export async function updateProject(
  projId: number,
  data: any,
): Promise<ProjectResponseDto> {
  try {
    const resp = await axios.put(`/projects/${projId}`, data);
    return resp.data;
  } catch (err) {
    console.error(err);
    throw err;
  }
}

export async function getProjectPermissions(projId: number) {
  try {
    const resp = await axios.get(`/projects/${projId}/permissions`);
    return resp.data;
  } catch (err) {
    console.error(err);
    throw err;
  }
}

export async function getProjectUsers(
  query: ProjectUserSearchQueryType,
): Promise<{ users: OrganizationUserDto[]; count: number }> {
  if (!query.proj_id) {
    throw new Error("proj_id is required in getProjectUsers query");
  }
  try {
    const resp = await axios.get(`/projects/${query.proj_id}/users`, {
      params: query,
    });
    return resp.data;
  } catch (err) {
    console.error(err);
    throw err;
  }
}

export async function createProjectUser(projId: number, data: any) {
  try {
    const resp = await axios.post(`/projects/${projId}/users/invite`, data);
    return resp.data;
  } catch (err) {
    console.error(err);
    throw err;
  }
}

export async function reinviteProjectUser(
  projId: number,
  userId: string,
  data: any,
) {
  try {
    const resp = await axios.post(
      `/projects/${projId}/users/${userId}/reinvite`,
      data,
    );
    return resp.data;
  } catch (err) {
    console.error(err);
    throw err;
  }
}

export async function removeUserFromProject(projId: number, userId: string) {
  try {
    const resp = await axios.delete(`/projects/${projId}/users/${userId}`);
    return resp.data;
  } catch (err) {
    console.error(err);
    throw err;
  }
}

export async function getProjectUserRoles(projId: number) {
  try {
    const resp = await axios.get(`/projects/${projId}/roles`);
    return resp.data;
  } catch (err) {
    console.error(err);
    throw err;
  }
}

export async function updateProjectUserRole(
  projId: number,
  userId: string,
  data: any,
) {
  try {
    const resp = await axios.put(
      `/projects/${projId}/users/${userId}/roles`,
      data,
    );
    return resp.data;
  } catch (err) {
    console.error(err);
    throw err;
  }
}

export async function getProjectHasActivePredictionRun(
  project_id: number,
): Promise<boolean> {
  try {
    const resp = await axios.get(
      `/projects/${project_id}/prediction_runs/has_active`,
    );
    return resp.data;
  } catch (err) {
    console.error(err);
    throw err;
  }
}

export async function getProjectHasActiveImportOrUpload(
  project_id: number,
): Promise<boolean> {
  try {
    const resp = await axios.get(
      `/projects/${project_id}/has_active_import_or_upload`,
    );
    return resp.data;
  } catch (err) {
    console.error(err);
    throw err;
  }
}

export async function getProjectPredictionRuns(
  project_id: number,
  limit = 10,
  offset = 0,
  sort_by = "created_at",
  sort_direction: "asc" | "desc" = "desc",
): Promise<PaginatedPredictionRunResponse> {
  try {
    const resp = await axios.get(`/projects/${project_id}/prediction_runs`, {
      params: { limit, offset, sort_by, sort_direction },
    });
    return resp.data;
  } catch (err) {
    console.error(err);
    throw err;
  }
}

// --- Project AI mappings & species list (AKL-679) ---------------------------

/**
 * List the project's effective mappings — project overrides plus each
 * relevant model's live global defaults for any label without a project
 * override. Omitting `modelId` returns the combined view across every model
 * enabled for this project (AKL-729); passing it scopes the result to that
 * one model only, exactly as before.
 * @param projectId numeric project id
 * @param modelId optional numeric model id — omitted for the combined view
 * @returns the effective mapping list and total count
 */
export async function getProjectMappings(
  projectId: number,
  modelId?: number,
): Promise<EffectiveMappingsResponse> {
  try {
    const resp = await axios.get(`/projects/${projectId}/mappings`, {
      params: modelId != null ? { model_id: modelId } : undefined,
    });
    return resp.data;
  } catch (err) {
    console.error("Error fetching project mappings:", err);
    throw err;
  }
}

/**
 * List the models currently enabled for a project (auto-enabled on first run,
 * or manually via `enableModelForProject`) — the project-scoped source for
 * the AI Mappings model filter and Model column (AKL-729), replacing the
 * unscoped global `getModels()`.
 * @param projectId numeric project id
 * @returns the enabled models list
 */
export async function getEnabledModelsForProject(
  projectId: number,
): Promise<EnabledModelsResponse> {
  try {
    const resp = await axios.get(`/projects/${projectId}/models`);
    return resp.data;
  } catch (err) {
    console.error("Error fetching enabled models:", err);
    throw err;
  }
}

/**
 * Manually enable a model for a project ahead of its first run, so its
 * mappings can be configured in advance (AKL-729). Additive to auto-enable —
 * a model still becomes enabled by simply being run, with no manual step
 * required.
 * @param projectId numeric project id
 * @param modelId numeric model id to enable
 * @returns the newly-enabled model
 */
export async function enableModelForProject(
  projectId: number,
  modelId: number,
): Promise<EnabledModelItem> {
  try {
    const resp = await axios.post(
      `/projects/${projectId}/models/${modelId}/enable`,
    );
    return resp.data;
  } catch (err) {
    console.error("Error enabling model for project:", err);
    throw err;
  }
}

/**
 * Disable an already-enabled model for a project: its predictions stop
 * surfacing (species list / default combined mappings view), but saved
 * observations and existing project mapping overrides are unaffected.
 * Reversible — re-enabling (manually, or by re-running the model) clears it.
 * @param projectId numeric project id
 * @param modelId numeric model id to disable
 * @returns the updated (now disabled) model
 */
export async function disableModelForProject(
  projectId: number,
  modelId: number,
): Promise<EnabledModelItem> {
  try {
    const resp = await axios.post(
      `/projects/${projectId}/models/${modelId}/disable`,
    );
    return resp.data;
  } catch (err) {
    console.error("Error disabling model for project:", err);
    throw err;
  }
}

/**
 * Add a custom project mapping (taxon) or an exclusion tombstone.
 * @param projectId numeric project id
 * @param body the mapping create request
 * @returns the created project mapping
 */
export async function addProjectMapping(
  projectId: number,
  body: ProjectMappingCreateRequest,
): Promise<ProjectMappingResponse> {
  try {
    const resp = await axios.post(`/projects/${projectId}/mappings`, body);
    return resp.data;
  } catch (err) {
    console.error("Error adding project mapping:", err);
    throw err;
  }
}

/**
 * Apply one mapping change to a whole list of `(model, raw label)` pairs in a
 * single request — the AKL-751 fix for the bulk Exclude / "Point all at one
 * species" actions, which previously fired one request per selected row and
 * saturated the browser and the API on a large label catalogue.
 *
 * Each entry is an upsert keyed on `(project_id, ai_model_id, raw_label)`, so
 * the caller neither sends a `mapping_id` nor decides create-vs-update, and the
 * server applies every valid row in one transaction. The response is a summary
 * (`applied_count` / `unchanged_count` / `skipped`) rather than per-row
 * outcomes: a row already in the requested state is counted as unchanged, not
 * as a failure.
 * @param projectId numeric project id
 * @param body the target kind (+ taxon for a repoint) and the labels to apply to
 * @returns the bulk-apply summary
 */
export async function bulkUpsertProjectMappings(
  projectId: number,
  body: BulkProjectMappingRequest,
): Promise<BulkProjectMappingResponse> {
  try {
    const resp = await axios.post(`/projects/${projectId}/mappings/bulk`, body);
    return resp.data;
  } catch (err) {
    console.error("Error bulk-upserting project mappings:", err);
    throw err;
  }
}

/**
 * Edit a project mapping (re-point taxon, set/clear display-name override,
 * exclude, or un-exclude).
 * @param projectId numeric project id
 * @param mappingId the project mapping id
 * @param body the mapping update request
 * @returns the updated project mapping
 */
export async function updateProjectMapping(
  projectId: number,
  mappingId: number,
  body: ProjectMappingUpdateRequest,
): Promise<ProjectMappingResponse> {
  try {
    const resp = await axios.patch(
      `/projects/${projectId}/mappings/${mappingId}`,
      body,
    );
    return resp.data;
  } catch (err) {
    console.error("Error updating project mapping:", err);
    throw err;
  }
}

/**
 * Delete a project-added mapping that has no global default. A row shadowing a
 * global default must be excluded, not deleted (the API rejects it with 400).
 * @param projectId numeric project id
 * @param mappingId the project mapping id
 */
export async function deleteProjectMapping(
  projectId: number,
  mappingId: number,
): Promise<{ detail: string }> {
  try {
    const resp = await axios.delete(
      `/projects/${projectId}/mappings/${mappingId}`,
    );
    return resp.data;
  } catch (err) {
    console.error("Error deleting project mapping:", err);
    throw err;
  }
}

/**
 * Reset a project mapping to its model's system default: deletes the project
 * override regardless of whether it shadows a global default (unlike
 * `deleteProjectMapping`, which rejects that case), falling back to the
 * model's current global default for that label.
 * @param projectId numeric project id
 * @param mappingId the project mapping id
 */
export async function resetProjectMapping(
  projectId: number,
  mappingId: number,
): Promise<{ detail: string }> {
  try {
    const resp = await axios.post(
      `/projects/${projectId}/mappings/${mappingId}/reset`,
    );
    return resp.data;
  } catch (err) {
    console.error("Error resetting project mapping:", err);
    throw err;
  }
}

/**
 * Reset a list of labels to their model defaults in a single request — the bulk
 * counterpart of `resetProjectMapping` (AKL-751 round 2), added so a bulk
 * exclusion can be undone in one pass rather than one request per row. Each
 * entry deletes the label's project override, so the model's global default
 * stops being shadowed and the label resolves to the model's own species again.
 *
 * A label with no project override is already at the model default and counts
 * as unchanged, not as a failure. A label whose override has no global default
 * behind it (a custom project mapping) is skipped with `no_global_default`
 * rather than deleted — resetting one would be a plain delete, which is why the
 * caller pre-filters those out too.
 * @param projectId numeric project id
 * @param body the labels to reset
 * @returns the bulk summary (`applied_count` / `unchanged_count` / `skipped`)
 */
export async function bulkResetProjectMappings(
  projectId: number,
  body: BulkResetMappingsRequest,
): Promise<BulkProjectMappingResponse> {
  try {
    const resp = await axios.post(
      `/projects/${projectId}/mappings/bulk/reset`,
      body,
    );
    return resp.data;
  } catch (err) {
    console.error("Error bulk-resetting project mappings:", err);
    throw err;
  }
}

/**
 * Fetch the project's effective species list — the union of active project
 * mappings and explicit species-list items, computed server-side.
 * @param projectId numeric project id
 * @returns the effective species list and total count
 */
export async function getSpeciesList(
  projectId: number,
): Promise<EffectiveSpeciesListResponse> {
  try {
    const resp = await axios.get(`/projects/${projectId}/species-list`);
    return resp.data;
  } catch (err) {
    console.error("Error fetching species list:", err);
    throw err;
  }
}

/**
 * Add a taxon to the project species list (the API lazily creates the list).
 * @param projectId numeric project id
 * @param body the species-list item create request
 * @returns the created species-list item
 */
export async function addSpeciesListItem(
  projectId: number,
  body: SpeciesListItemCreateRequest,
): Promise<SpeciesListItemResponse> {
  try {
    const resp = await axios.post(
      `/projects/${projectId}/species-list/items`,
      body,
    );
    return resp.data;
  } catch (err) {
    console.error("Error adding species list item:", err);
    throw err;
  }
}

/**
 * Remove a manually-added species-list item.
 * @param projectId numeric project id
 * @param itemId the species-list item id
 */
export async function removeSpeciesListItem(
  projectId: number,
  itemId: number,
): Promise<{ detail: string }> {
  try {
    const resp = await axios.delete(
      `/projects/${projectId}/species-list/items/${itemId}`,
    );
    return resp.data;
  } catch (err) {
    console.error("Error removing species list item:", err);
    throw err;
  }
}
